import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, BadgeCheck, Star, Heart } from "lucide-react";
import { cuisineStyle } from "../lib/cuisines";
import { mediaUrl } from "../lib/api";
import { useFavorites } from "../hooks/useFavorites";

export const TruckCard = ({ truck, index = 0 }) => {
  const { Icon, bg, fg } = cuisineStyle(truck.cuisine);
  const photo = truck.photos && truck.photos.length ? mediaUrl(truck.photos[0]) : null;
  const premium = truck.plan === "pro" || truck.plan === "proplus";
  const { isFav, toggle } = useFavorites();
  const fav = isFav(truck.slug);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (index % 4) * 0.06 }}
      className="relative h-full bg-white rounded-2xl shadow-warm overflow-hidden flex flex-col hover:shadow-warm-lg hover:-translate-y-1 transition-transform duration-200"
      data-testid={`truck-card-${truck.slug}`}
    >
      <button
        onClick={(e) => { e.preventDefault(); toggle(truck.slug); }}
        className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-warm hover:scale-110 transition-transform"
        data-testid={`fav-btn-${truck.slug}`} aria-label="Favori"
      >
        <Heart size={17} className={fav ? "fill-coral-twist text-coral-twist" : "text-urban-teal/50"} />
      </button>
      <Link to={`/foodtruck/${truck.slug}`} className="relative block h-44">
        {photo ? (
          <img src={photo} alt={truck.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: bg }}>
            <Icon size={56} strokeWidth={1.8} style={{ color: fg }} />
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          {premium && (
            <span className="px-2.5 py-1 rounded-full bg-street-gold text-white text-[10px] font-bold uppercase tracking-wide flex items-center gap-1"><Star size={11} className="fill-white" /> Premium</span>
          )}
          {truck.is_open ? (
            <span className="px-2.5 py-1 rounded-full bg-fresh-mint text-white text-[10px] font-bold uppercase">Ouvert</span>
          ) : (
            <span className="px-2.5 py-1 rounded-full bg-coral-twist text-white text-[10px] font-bold uppercase">Fermé</span>
          )}
        </div>
      </Link>
      <div className="p-4 flex flex-col flex-1">
        <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: fg }}>{truck.cuisine}</span>
        <div className="flex items-start justify-between gap-2 mt-0.5">
          <div className="flex items-start gap-1 min-w-0">
            <h3 className="font-display font-bold text-lg leading-tight text-urban-teal line-clamp-2">{truck.name}</h3>
            {truck.verified && <BadgeCheck size={16} className="text-fresh-mint shrink-0 mt-1" />}
          </div>
          {truck.rating_count > 0 && (
            <div className="flex items-center gap-1 text-sm shrink-0 mt-0.5" data-testid={`truck-rating-${truck.slug}`}><Star size={13} className="fill-street-gold text-street-gold" /><span className="font-bold text-urban-teal">{truck.rating_avg}</span></div>
          )}
        </div>
        <div className="text-sm text-urban-teal/60 flex items-center gap-1 mt-1"><MapPin size={13} className="shrink-0" /> <span className="truncate">{truck.city}{truck.distance !== undefined && truck.distance < 9999 ? ` · ${truck.distance} km` : ""}</span></div>
        {truck.description && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{truck.description}</p>}
        <Link to={`/foodtruck/${truck.slug}`} className="mt-auto pt-3 text-street-gold font-bold text-sm flex items-center gap-1 hover:gap-2 transition-all" data-testid={`truck-link-${truck.slug}`}>
          Voir la fiche <ArrowRight size={15} />
        </Link>
      </div>
    </motion.div>
  );
};
